import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import { ProgramHero } from "@/components/site/programs/ProgramHero";
import { MENTORSHIP } from "@/content/programs";

const PhaseRow = ({ phase, isLast, index }) => (
  <Reveal delay={0.04 * index}>
    <div data-testid={`mentorship-phase-${phase.n}`} className="relative flex gap-5 sm:gap-7">
      {/* Number + connector */}
      <div className="flex flex-col items-center">
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--wf-maroon)] font-serif text-xl font-semibold text-white shadow-[var(--wf-shadow-sm)] ring-4 ring-[var(--wf-maroon)]/12">
          {phase.n}
        </span>
        {!isLast && <span aria-hidden="true" className="mt-2 w-px flex-1 bg-[var(--wf-border)]" />}
      </div>
      {/* Content */}
      <div className={isLast ? "pb-0" : "pb-10 sm:pb-14"}>
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[1fr_260px] md:gap-8 lg:grid-cols-[1fr_300px]">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--wf-green-text)]">Phase {phase.n}</span>
            <h3 className="mt-1 font-serif text-xl font-semibold text-[var(--wf-ink)] sm:text-2xl">{phase.title}</h3>
            <p className="mt-3 max-w-[62ch] text-sm leading-7 text-[var(--wf-soil)] sm:text-base">{phase.details}</p>
          </div>
          {phase.image && (
            <ImageWithCredit
              src={phase.image}
              alt={phase.imageAlt}
              className="aspect-[4/3] w-full rounded-sm shadow-[var(--wf-shadow-sm)] md:mt-1"
              imgClassName="object-cover"
            />
          )}
        </div>
      </div>
    </div>
  </Reveal>
);

export default function MentorshipProgramPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <ProgramHero
          eyebrow={MENTORSHIP.hero.eyebrow}
          title={MENTORSHIP.hero.title}
          lede={MENTORSHIP.hero.lede}
          image={MENTORSHIP.hero.image}
          imageAlt={MENTORSHIP.hero.imageAlt}
          back={{ label: "All programs", href: "/programs" }}
        />

        {/* Intro */}
        <section id="mentorship-intro" aria-labelledby="mentorship-intro-heading" className="wf-section bg-[var(--wf-cream)]">
          <div className="wf-container">
            <Reveal className="mx-auto max-w-3xl text-center">
              <Kicker className="justify-center">{MENTORSHIP.intro.eyebrow}</Kicker>
              <h2 id="mentorship-intro-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
                {MENTORSHIP.intro.title}
              </h2>
              <p className="mt-5 text-base leading-7 text-[var(--wf-soil)]">{MENTORSHIP.intro.body}</p>
            </Reveal>
          </div>
        </section>

        {/* Phases */}
        <section id="mentorship-phases" aria-label="The five phases" className="wf-section bg-white">
          <div className="wf-container">
            <div className="mx-auto max-w-4xl">
              {MENTORSHIP.phases.map((p, i) => (
                <PhaseRow key={p.n} phase={p} index={i} isLast={i === MENTORSHIP.phases.length - 1} />
              ))}
            </div>
          </div>
        </section>

        {/* Cross-link to Flats */}
        <section aria-labelledby="mentorship-next-heading" className="bg-[var(--wf-cream)] py-16 sm:py-20">
          <div className="wf-container">
            <Reveal>
              <Link
                to="/programs/flats-mentor-farm"
                data-testid="mentorship-to-flats-link"
                className="group flex flex-col items-start justify-between gap-6 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-wheat)] p-8 transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background sm:flex-row sm:items-center sm:p-10"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--wf-maroon)]">The land</span>
                  <h2 id="mentorship-next-heading" className="mt-2 font-serif text-2xl font-semibold text-[var(--wf-ink)] sm:text-3xl">Flats Mentor Farm</h2>
                  <p className="mt-2 max-w-[52ch] text-sm leading-6 text-[var(--wf-soil)]">See where it all happens \u2014 70 acres of shared land, infrastructure, and support in Lancaster, MA.</p>
                </div>
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-white transition-colors group-hover:bg-[var(--wf-maroon-dark)]">
                  Explore the Flats
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Closing CTA */}
        <section aria-labelledby="mentorship-outro-heading" className="bg-white py-16 sm:py-20 lg:py-24">
          <div className="wf-container">
            <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-wheat)]/20 blur-2xl" />
              <div className="relative mx-auto max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Help farmers thrive</span>
                <h2 id="mentorship-outro-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Support the next phase</h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">Your gift fuels the training, land access, and mentorship that move farmers toward independence.</p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <WfButton as="a" href="/support" variant="light" withArrow className="px-7" data-testid="mentorship-outro-support-button">Support a farmer</WfButton>
                  <WfButton as="a" href="/programs" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="mentorship-outro-programs-button">All programs</WfButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
